import React from "react";
import { ClientSetupDialog } from "./BillingDirectoryView.jsx";
import { Link } from "react-router";
import { ChevronDown, FileText, MessageSquareText } from "lucide-react";
import BillableCalculationPanel from "./BillableCalculationPanel.jsx";
import { useBillingClientExplanation } from "./BillingClientExplanationLauncher.jsx";
import ContextMenu, { ContextMenuItem, useContextMenu } from "../ContextMenu.jsx";
import { groupBillableAccountsForCards } from "../../utils/billingBillableGrouping.js";
import { billableDompeurTons, billableHoursTotal, formatBillingNumber, formatBillingQuantity } from "../../utils/billingCalculation.js";
import { billingDraftTargets } from "../../utils/billingDraftSelection.js";
import {
  billableAccountBlockingCodes,
  billableBlockingFix,
  billableBlockingReasonShortLabel,
} from "../../utils/billingPresentation.js";
import { buttonToneClasses, DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME, joinClasses } from "../resultsPrimitives/shared.js";

const PILL_CLASS_NAME = DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME.replace("!", "");
const PRIMARY_BUTTON = joinClasses([
  "inline-flex h-7 shrink-0 items-center justify-center gap-1 border px-2 text-[11px] font-extrabold transition disabled:cursor-not-allowed disabled:opacity-45",
  PILL_CLASS_NAME,
  buttonToneClasses.optimizer,
]);

function formatMoney(value, currency = "CAD") {
  if (value == null) return "—";
  return new Intl.NumberFormat("fr-CA", { style: "currency", currency: currency || "CAD" }).format(Number(value || 0));
}

function formatTotals(totalsByCurrency) {
  if (!totalsByCurrency?.length) return formatMoney(0);
  return totalsByCurrency.map(({ currencyCode, amount }) => formatMoney(amount, currencyCode)).join(" · ");
}

const shortDay = (value) => new Intl.DateTimeFormat("fr-CA", { day: "numeric", month: "short", timeZone: "UTC" }).format(new Date(`${value}T12:00:00Z`));

export default function BillableAccountCards({
  items = [],
  canDraft = false,
  busy = false,
  onCreate,
  sourceClients = [], billingAccounts = [], canAssociate = false, onAssociate, onCreateAccount, onReload,
}) {
  const accounts = React.useMemo(() => groupBillableAccountsForCards(items), [items]);
  const [expandedAccountKey, setExpandedAccountKey] = React.useState(null);
  const panelId = React.useId();
  const [associationClient, setAssociationClient] = React.useState(null);
  const menu = useContextMenu();
  const { canRead, openExplanation, explanationDialog } = useBillingClientExplanation();
  const clientFor = (account) => {
    const ids = [...new Set(account.items.map((item) => item.source_client_correspondence_id || item.source_client?.id).filter(Boolean))];
    return canAssociate && ids.length === 1 ? sourceClients.find((client) => client.id === ids[0]) : null;
  };
  const canOpenAssociation = (account) => Boolean(clientFor(account));
  const openAssociation = (account) => setAssociationClient(clientFor(account));
  const expandedAccount = accounts.find((account) => account.key === expandedAccountKey);
  React.useEffect(() => {
    if (!expandedAccount) setExpandedAccountKey(null);
  }, [expandedAccount]);

  return (
    <div className="billable-accounts-workspace">
      <div className="billable-account-list">
      <p className="mb-1.5 text-[11px] text-slate-500">Les prestations sont regroupées par entreprise payeuse.</p>
      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 xl:grid-cols-4">
        {accounts.map((account) => {
          const blockingCodes = billableAccountBlockingCodes(account.items);
          const hours = billableHoursTotal(account.items);
          const tons = billableDompeurTons(account.items);
          const canCreate = canDraft && account.eligibleCount > 0;
          // Un brouillon couvre déjà ces dates : « Ajouter au brouillon » plutôt qu'un 2e brouillon.
          const targets = canCreate ? billingDraftTargets(account.items.filter((item) => account.eligibleIds.includes(item.id))) : [];
          const expanded = expandedAccountKey === account.key;
          return (
            <article
              key={account.key}
              onContextMenu={(event) => { if (canRead && !busy) menu.openAt(event, account); }}
              className={joinClasses([
                "relative flex flex-col gap-1 border bg-white px-2.5 py-1.5",
                PILL_CLASS_NAME,
                expanded ? "border-accent ring-1 ring-accent" : "border-slate-200",
                account.eligibleCount ? "" : "bg-amber-50/40",
              ])}
            >
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="min-w-0 text-sm font-extrabold leading-tight text-slate-900">
                  <button type="button" aria-expanded={expanded} aria-controls={panelId}
                    className="min-w-0 cursor-pointer text-left outline-none after:absolute after:inset-0 after:rounded-[4px] after:content-[''] hover:text-accent focus-visible:after:outline-2 focus-visible:after:outline-accent"
                    onClick={() => setExpandedAccountKey(expanded ? null : account.key)}>
                    <span className="line-clamp-2">{account.label}</span>
                  </button>
                </h3>
                {hours !== undefined || tons !== undefined ? <span className="ml-auto shrink-0 text-[12px] tabular-nums leading-tight text-slate-600">
                  {[hours !== undefined ? formatBillingQuantity(hours, "heure").replace("≈ ", "") : null, tons !== undefined ? `${formatBillingNumber(tons, 3)} t` : null].filter(Boolean).join(" · ")}
                </span> : null}
                <strong className="shrink-0 text-sm font-extrabold tabular-nums leading-tight text-slate-900">
                  {formatTotals(account.totalsByCurrency)}
                </strong>
                <ChevronDown size={13} aria-hidden="true" className={`shrink-0 text-slate-500 transition-transform ${expanded ? "rotate-180" : ""}`} />
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <p className="text-[11px] leading-tight text-slate-600">
                  {[account.eligibleCount ? "Prêtes à facturer" : "", account.blockedCount ? "À corriger" : ""].filter(Boolean).join(" · ")}
                </p>
                {canCreate ? (
                  <span className="relative z-10 ml-auto flex gap-1">
                    {targets.map(({ draft, ids }) => (
                      <button key={draft?.id || "new"} type="button" className={PRIMARY_BUTTON} disabled={busy} onClick={() => onCreate?.(ids)}>
                        <FileText size={12} />
                        {draft ? "Ajouter au brouillon" : "Créer le brouillon"}
                      </button>
                    ))}
                  </span>
                ) : null}
              </div>
              {targets.filter(({ draft }) => draft).map(({ draft }) => (
                <p key={draft.id} className="text-[11px] leading-tight text-slate-600">
                  <strong className="font-extrabold text-accent">Brouillon du {shortDay(draft.period_start)} au {shortDay(draft.period_end)}</strong>
                  {` · ${formatMoney(draft.total)}`}
                </p>
              ))}
              {blockingCodes.length ? (
                <ul className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] leading-tight text-amber-900">
                  {blockingCodes.map((code) => {
                    const fix = billableBlockingFix(code);
                    return (
                      <li key={code}>
                        {billableBlockingReasonShortLabel(code) || "À corriger"}
                        {fix ? (
                          <>
                            {" · "}
                            {fix.label === "Associer" && canOpenAssociation(account) ? <button type="button" disabled={busy}
                              className="relative z-10 rounded-[4px] border border-slate-300 bg-white px-1 font-bold text-accent hover:border-slate-400 focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-50"
                              onClick={() => openAssociation(account)}>Associer</button> : <Link className="relative z-10 font-bold text-accent underline-offset-2 hover:underline" to={fix.href}>
                              {fix.label}
                            </Link>}
                          </>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </article>
          );
        })}
      </div>
      </div>
      {expandedAccount ? <BillableCalculationPanel key={expandedAccount.key} account={expandedAccount} id={panelId} onExplain={() => openExplanation(expandedAccount.items)} onAssociate={canOpenAssociation(expandedAccount) ? () => openAssociation(expandedAccount) : undefined} busy={busy} /> : null}
      <ContextMenu menu={menu} label="Actions des prestations" title={menu.data?.label}>
        {menu.data && canRead ? <ContextMenuItem icon={MessageSquareText} disabled={busy} onSelect={() => openExplanation(menu.data.items)}>Expliquer au client</ContextMenuItem> : null}
      </ContextMenu>
      {explanationDialog}
      {associationClient ? <ClientSetupDialog client={associationClient} accounts={billingAccounts} busy={busy}
        onClose={() => setAssociationClient(null)} onAssociate={onAssociate} onReload={onReload}
        onCreate={(client) => { setAssociationClient(null); onCreateAccount(client); }} /> : null}
    </div>
  );
}
